import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ACCESS_ROLES, mayManageMember, roleLabel } from '@aot-aiops/shared';
import { api } from './api';
import { formatInActiveTimeZone } from './timezone';
import { TurnstileWidget } from './turnstile';
import { FederatedButtons } from './sign-in-methods';
import './user-access.css';

type Member = {
  id: string;
  name: string;
  email: string;
  role: string;
  state: string;
  mfa: number;
  lastActive: string | null;
  signInMethods?: string;
  assignmentReason?: string;
};
type Invitation = {
  id: string;
  name: string;
  email: string;
  role: string;
  state: string;
  sentAt: string;
  expiresAt: string;
  invitedBy: string;
  deliveryState: string;
};
type AccessData = {
  members: Member[];
  invitations: Invitation[];
  invitableRoles: string[];
  staff: boolean;
};
const human = (value: string) =>
  ({
    ACTIVE: 'Active',
    INVITED: 'Invited',
    MFA_REQUIRED: 'MFA required',
    SUSPENDED: 'Suspended',
    REMOVED: 'Removed',
    ACCEPTED: 'Accepted',
    REVOKED: 'Revoked',
    EXPIRED: 'Expired',
    PENDING: 'Pending',
    FAILED: 'Delivery failed',
    ACCEPTED_BY_PROVIDER: 'Accepted by email provider',
  })[value] ?? 'Unavailable';
export function UsersAccess({
  tenantId,
  role,
  actorUserId,
  refreshVersion = 0,
}: {
  tenantId: string;
  role: string;
  actorUserId?: string;
  refreshVersion?: number;
}) {
  const [search, setSearch] = useState(''),
    [roleFilter, setRoleFilter] = useState(''),
    [stateFilter, setStateFilter] = useState('');
  const [data, setData] = useState<AccessData | null>(null),
    [tab, setTab] = useState('Users'),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false),
    [inviting, setInviting] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    id: string;
    action: string;
    name: string;
    role?: string;
  } | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (confirmation) dialogRef.current?.showModal();
  }, [confirmation]);
  const base = `/v1/access/tenants/${encodeURIComponent(tenantId)}`;
  const load = async () => setData(await api<AccessData>(base));
  useEffect(() => {
    if (busy) return;
    let current = true;
    void api<AccessData>(base)
      .then((next) => {
        if (current) setData(next);
      })
      .catch((e) => {
        if (current)
          setMessage(e instanceof Error ? e.message : 'Workspace access could not be refreshed.');
      });
    return () => {
      current = false;
    };
  }, [tenantId, refreshVersion, busy]);
  async function perform(path: string, body?: unknown) {
    setBusy(true);
    setMessage('');
    try {
      const result = await api<{ message?: string }>(`${base}${path}`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
      await load();
      setConfirmation(null);
      setInviting(false);
      setMessage(result.message ?? 'Workspace access updated.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'The update could not be completed.');
    } finally {
      setBusy(false);
    }
  }
  async function invite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const values = new FormData(e.currentTarget);
    await perform('/invitations', {
      displayName: values.get('name'),
      email: values.get('email'),
      role: values.get('role'),
    });
  }
  const staff = data?.staff ?? false;
  return (
    <section className="access-panel" aria-label="Users & Access">
      <header>
        <div>
          <h2>Users &amp; Access</h2>
          <p>
            Manage workspace membership. Every user chooses their own password and secures their
            account with MFA.
          </p>
        </div>
        {!!data?.invitableRoles.length && (
          <button className="button primary" onClick={() => setInviting(!inviting)}>
            Invite user
          </button>
        )}
      </header>
      {message && (
        <p role="status" className="toast">
          {message}
        </p>
      )}
      <nav className="access-tabs" aria-label="Access sections">
        {['Users', 'Invitations', 'AOT Access', 'Roles'].map((t) => (
          <button key={t} aria-pressed={tab === t} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </nav>
      {inviting && (
        <form className="access-form" onSubmit={invite}>
          <h3>{role === 'aot_admin' && !staff ? 'Invite Customer Owner' : 'Invite user'}</h3>
          <label>
            Name
            <input name="name" required minLength={2} maxLength={150} />
          </label>
          <label>
            Email
            <input name="email" type="email" required maxLength={254} />
          </label>
          <label>
            Role
            <select name="role">
              {data?.invitableRoles.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </label>
          <p>The single-use invitation expires after 72 hours. No password is sent.</p>
          <button className="button primary" disabled={busy}>
            Send invitation
          </button>
          <button type="button" className="button secondary" onClick={() => setInviting(false)}>
            Cancel
          </button>
        </form>
      )}
      {!data && <p>Loading workspace access…</p>}
      {tab === 'AOT Access' && role === 'aot_admin' && !staff && (
        <form
          className="access-form"
          onSubmit={(e) => {
            e.preventDefault();
            const fields = new FormData(e.currentTarget);
            void perform('/staff', {
              email: fields.get('email'),
              reason: fields.get('reason'),
              action: fields.get('action'),
            });
          }}
        >
          <h3>AOT operations assignment</h3>
          <label>
            AOT staff email
            <input name="email" type="email" required />
          </label>
          <label>
            Reason / assignment
            <input name="reason" required minLength={5} maxLength={300} />
          </label>
          <label>
            Action
            <select name="action">
              <option value="assign">Assign access</option>
              <option value="remove">Remove assignment</option>
            </select>
          </label>
          <p>
            Only existing active AOT operations staff with MFA can be assigned. This does not create
            a customer employee account.
          </p>
          <button disabled={busy}>Save assignment</button>
        </form>
      )}
      {data && (tab === 'Users' || tab === 'AOT Access') && (
        <div className="access-actions">
          <label>
            Search users
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or email"
            />
          </label>
          <label>
            Filter by role
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">All roles</option>
              {Object.keys(ACCESS_ROLES).map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Filter by status
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              <option value="">All statuses</option>
              {['ACTIVE', 'MFA_REQUIRED', 'SUSPENDED', 'REMOVED'].map((s) => (
                <option key={s} value={s}>
                  {human(s)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {data && (tab === 'Users' || tab === 'AOT Access') && (
        <>
          {tab === 'AOT Access' && (
            <p>
              AOT staff access is assigned separately from your team. Operational access is recorded
              in the audit history.
            </p>
          )}
          <div className="access-cards">
            {data.members
              .filter(
                (m) =>
                  (!roleFilter || m.role === roleFilter) &&
                  (!stateFilter || m.state === stateFilter) &&
                  `${m.name} ${m.email}`.toLowerCase().includes(search.toLowerCase()),
              )
              .filter((m) =>
                tab === 'AOT Access'
                  ? m.role.startsWith('aot_')
                  : staff || !m.role.startsWith('aot_'),
              )
              .map((m) => (
                <article key={m.id}>
                  <h3>{m.name}</h3>
                  <p className="access-email">{m.email}</p>
                  <dl>
                    <dt>Role</dt>
                    <dd>{roleLabel(m.role)}</dd>
                    <dt>Status</dt>
                    <dd>{human(m.state)}</dd>
                    <dt>MFA</dt>
                    <dd>{m.mfa ? 'Enabled' : 'Required'}</dd>
                    <dt>Last active</dt>
                    <dd>
                      {m.lastActive
                        ? formatInActiveTimeZone(m.lastActive)
                        : 'Recent sign-in information unavailable'}
                    </dd>
                    <dt>Sign-in methods</dt>
                    <dd>{m.signInMethods || 'No method connected'}</dd>
                    {m.role.startsWith('aot_') && (
                      <>
                        <dt>Assignment</dt>
                        <dd>
                          {m.assignmentReason ||
                            'Contact the platform administrator for assignment details.'}
                        </dd>
                      </>
                    )}
                  </dl>
                  {m.id !== actorUserId && mayManageMember(role, m.role, staff) && (
                    <div className="access-actions">
                      <label>
                        Change role
                        <select
                          value=""
                          disabled={busy}
                          onChange={(e) =>
                            setConfirmation({
                              id: m.id,
                              name: m.name,
                              action: 'role',
                              role: e.target.value,
                            })
                          }
                        >
                          <option value="">Choose role</option>
                          {data.invitableRoles.map((r) => (
                            <option value={r} key={r}>
                              {roleLabel(r)}
                            </option>
                          ))}
                        </select>
                      </label>
                      {role === 'tenant_owner' &&
                        m.state === 'ACTIVE' &&
                        m.role !== 'tenant_owner' && (
                          <button
                            onClick={() =>
                              setConfirmation({ id: m.id, name: m.name, action: 'transfer' })
                            }
                          >
                            Transfer ownership
                          </button>
                        )}
                      {['suspend', 'reactivate', 'remove', 'mfa-recovery']
                        .filter((a) =>
                          a === 'reactivate'
                            ? m.state === 'SUSPENDED'
                            : a === 'suspend'
                              ? m.state === 'ACTIVE'
                              : true,
                        )
                        .map((action) => (
                          <button
                            key={action}
                            disabled={busy}
                            onClick={() => setConfirmation({ id: m.id, name: m.name, action })}
                          >
                            {
                              (
                                {
                                  suspend: 'Suspend access',
                                  reactivate: 'Reactivate access',
                                  remove: 'Remove from workspace',
                                  'mfa-recovery': 'Request MFA re-enrollment',
                                } as Record<string, string>
                              )[action]
                            }
                          </button>
                        ))}
                    </div>
                  )}
                </article>
              ))}
          </div>
          {!data.members.some((m) =>
            tab === 'AOT Access' ? m.role.startsWith('aot_') : staff || !m.role.startsWith('aot_'),
          ) && (
            <p>
              {tab === 'AOT Access'
                ? 'No AOT operational access is assigned.'
                : 'Invite team members to give them access to this workspace.'}
            </p>
          )}
        </>
      )}
      {data && tab === 'Invitations' && (
        <div className="access-cards">
          {!data.invitations.some((i) => ['INVITED', 'MFA_REQUIRED'].includes(i.state)) && (
            <p>No pending invitations.</p>
          )}
          {data.invitations.map((i) => (
            <article key={i.id}>
              <h3>{i.name}</h3>
              <p className="access-email">{i.email}</p>
              <dl>
                <dt>Role</dt>
                <dd>{roleLabel(i.role)}</dd>
                <dt>Status</dt>
                <dd>{human(i.state)}</dd>
                <dt>Invited by</dt>
                <dd>{i.invitedBy}</dd>
                <dt>Sent</dt>
                <dd>{formatInActiveTimeZone(i.sentAt)}</dd>
                <dt>Expires</dt>
                <dd>{formatInActiveTimeZone(i.expiresAt)}</dd>
                <dt>Delivery</dt>
                <dd>{human(i.deliveryState)}</dd>
              </dl>
              {['INVITED', 'MFA_REQUIRED', 'EXPIRED'].includes(i.state) &&
                data.invitableRoles.includes(i.role) && (
                  <div className="access-actions">
                    {i.state !== 'MFA_REQUIRED' && (
                      <button
                        disabled={busy}
                        onClick={() => void perform(`/invitations/${i.id}/resend`)}
                      >
                        Resend
                      </button>
                    )}
                    <button
                      disabled={busy}
                      onClick={() => void perform(`/invitations/${i.id}/revoke`)}
                    >
                      Revoke
                    </button>
                  </div>
                )}
            </article>
          ))}
        </div>
      )}
      {tab === 'Roles' && (
        <div className="access-cards">
          {Object.entries(ACCESS_ROLES)
            .filter(([key]) => staff || key.startsWith('tenant_'))
            .map(([key, r]) => (
              <article key={key}>
                <h3>{r.label}</h3>
                <p>{r.capabilities}</p>
                <small>MFA required</small>
              </article>
            ))}
        </div>
      )}
      {confirmation && (
        <dialog
          ref={dialogRef}
          onCancel={() => setConfirmation(null)}
          className="access-confirm"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm access change"
        >
          <h3>Confirm access change</h3>
          {message && <p role="alert">{message}</p>}
          <p>
            Update access for {confirmation.name}
            {confirmation.role ? ` to ${roleLabel(confirmation.role)}` : ''}?
          </p>
          {confirmation.action === 'transfer' && (
            <p>
              This person becomes an owner. You remain an owner. At least one active owner must
              remain.
            </p>
          )}
          <p>Existing sessions may be signed out. Historical activity is retained.</p>
          <button
            className="button primary"
            disabled={busy}
            onClick={() =>
              void perform(`/members/${confirmation.id}`, {
                action: confirmation.action,
                ...(confirmation.role ? { role: confirmation.role } : {}),
              })
            }
          >
            Confirm
          </button>
          <button
            className="button secondary"
            disabled={busy}
            onClick={() => setConfirmation(null)}
          >
            Cancel
          </button>
        </dialog>
      )}
    </section>
  );
}

export function CreateCustomerForm({
  onCreated,
  onCancel,
}: {
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false);
  return (
    <form
      className="access-form"
      onSubmit={(e) => {
        e.preventDefault();
        const form = new FormData(e.currentTarget);
        setBusy(true);
        void api<{ id: string }>('/v1/aot/tenants', {
          method: 'POST',
          body: JSON.stringify({
            companyName: form.get('name'),
            displayName: form.get('name'),
            slug: form.get('slug'),
            country: form.get('country'),
            timeZone: form.get('timezone'),
            operationalContact: form.get('contact'),
            status: 'trial',
            accessClass: form.get('purpose'),
          }),
        })
          .then((v) => onCreated(v.id))
          .catch((e) => setMessage(e.message))
          .finally(() => setBusy(false));
      }}
    >
      <h2>Create Customer</h2>
      <p>
        Prepare the workspace, then invite its Customer Owner. This does not activate a subscription
        or monitoring.
      </p>
      {message && <p role="alert">{message}</p>}
      <label>
        Organization name
        <input name="name" required minLength={2} maxLength={200} />
      </label>
      <label>
        Workspace address name
        <input
          name="slug"
          required
          pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
          placeholder="organization-name"
        />
      </label>
      <label>
        Country
        <input name="country" defaultValue="PH" required minLength={2} maxLength={2} />
      </label>
      <label>
        Workspace timezone
        <input name="timezone" required defaultValue="Asia/Manila" />
      </label>
      <label>
        Primary contact
        <input name="contact" required type="email" />
      </label>
      <label>
        Workspace purpose
        <select name="purpose" defaultValue="CERTIFICATION">
          <option value="CERTIFICATION">Staging certification</option>
          <option value="CUSTOMER">Customer workspace</option>
        </select>
      </label>
      <button className="button primary" disabled={busy}>
        Create workspace
      </button>
      <button className="button secondary" type="button" onClick={onCancel}>
        Cancel
      </button>
    </form>
  );
}

// URL fragment is cleared immediately; token stays only in this page's memory.
export const arrivalInvitationToken =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('invitation')
    ? window.location.hash.slice(1)
    : '';
if (arrivalInvitationToken) window.history.replaceState(null, '', '/?invitation=1');
export function AcceptInvitation({
  token,
  signedIn,
  onSignIn,
}: {
  token: string;
  signedIn: boolean;
  onSignIn: () => void;
}) {
  const [data, setData] = useState<any>(null),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false),
    [verification, setVerification] = useState(''),
    [siteKey, setSiteKey] = useState<string | null>(null);
  useEffect(() => {
    void api('/v1/auth/invitations/inspect', { method: 'POST', body: JSON.stringify({ token }) })
      .then(setData)
      .catch((e) => setMessage(e.message));
    void api<any>('/v1/auth/config').then((c) => setSiteKey(c.turnstileSiteKey));
  }, [token]);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    const form = new FormData(e.currentTarget);
    try {
      if (signedIn) {
        await api('/v1/access/accept', { method: 'POST', body: JSON.stringify({ token }) });
        window.location.assign('/');
        return;
      }
      if (form.get('password') !== form.get('confirm'))
        throw new Error('The passphrases do not match.');
      const result = await api<{ csrfToken: string }>('/v1/auth/invitations/register', {
        method: 'POST',
        body: JSON.stringify({
          token,
          password: form.get('password'),
          turnstileToken: verification,
        }),
      });
      sessionStorage.setItem('aot_csrf', result.csrfToken);
      window.location.assign('/settings?section=Security');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Invitation could not be accepted.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="access-invitation">
      <p className="eyebrow">AOT OPSAI</p>
      <h1>Your workspace invitation</h1>
      {message && <p role="alert">{message}</p>}
      {data && (
        <>
          <h2>{data.workspace}</h2>
          <p>
            {data.name}, you are invited as {roleLabel(data.role)}.
          </p>
          <p>Expires {formatInActiveTimeZone(data.expiresAt)}.</p>
          {!signedIn && <FederatedButtons invitationToken={token} />}
          <form className="access-form" onSubmit={submit}>
            {!signedIn && !data.existingUser && (
              <>
                <label>
                  Choose a passphrase
                  <input
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    minLength={15}
                    maxLength={128}
                    required
                  />
                </label>
                <label>
                  Confirm passphrase
                  <input
                    name="confirm"
                    type="password"
                    autoComplete="new-password"
                    minLength={15}
                    required
                  />
                </label>
                <TurnstileWidget
                  siteKey={siteKey}
                  action="invitation"
                  theme="light"
                  onToken={setVerification}
                />
                <p>
                  Next, enroll your authenticator. Workspace access starts only after MFA and
                  acceptance.
                </p>
              </>
            )}
            {signedIn ? (
              <button className="button primary" disabled={busy}>
                Accept workspace membership
              </button>
            ) : data.existingUser ? (
              <button type="button" className="button primary" onClick={onSignIn}>
                Sign in to accept
              </button>
            ) : (
              <button
                className="button primary"
                disabled={busy || Boolean(siteKey && !verification)}
              >
                Continue to MFA
              </button>
            )}
          </form>
          {!signedIn && !data.existingUser && (
            <button onClick={onSignIn}>Already have an account? Sign in</button>
          )}
        </>
      )}
    </main>
  );
}
export function PendingMemberships() {
  const [items, setItems] = useState<any[]>([]),
    [message, setMessage] = useState('');
  useEffect(() => {
    void api<any>('/v1/access/pending')
      .then((v) => setItems(v.items))
      .catch(() => undefined);
  }, []);
  if (!items.length) return null;
  return (
    <section className="access-panel">
      <h3>Complete your workspace membership</h3>
      <p>After enrolling MFA below, accept your invitation to activate access.</p>
      {message && <p role="status">{message}</p>}
      {items.map((i) => (
        <button
          className="button secondary"
          key={i.id}
          onClick={() => {
            void api('/v1/access/accept', { method: 'POST', body: JSON.stringify({ id: i.id }) })
              .then(() => window.location.assign('/'))
              .catch((e) => setMessage(e.message));
          }}
        >
          Accept {i.workspace} membership
        </button>
      ))}
    </section>
  );
}
export function WorkspaceSwitcher({
  tenantId,
  role,
  name,
}: {
  tenantId: string;
  role: string;
  name: string;
}) {
  const [items, setItems] = useState<any[]>([]),
    [message, setMessage] = useState(''),
    [reason, setReason] = useState('');
  useEffect(() => {
    void api<any>('/v1/access/workspaces')
      .then((v) => setItems(v.items))
      .catch(() => undefined);
  }, [tenantId]);
  const current = items.find((i) => i.id === tenantId);
  return (
    <div className="access-workspaces">
      {role.startsWith('aot_') && current && current.accessClass !== 'AOT_INTERNAL' && (
        <p role="status">AOT Operations access — {name}</p>
      )}
      {items.length > 1 && (
        <>
          <label>
            Workspace
            <select
              value={tenantId}
              onChange={(e) => {
                void api('/v1/access/workspaces/switch', {
                  method: 'POST',
                  body: JSON.stringify({ tenantId: e.target.value, ...(reason ? { reason } : {}) }),
                })
                  .then(() => window.location.assign('/overview'))
                  .catch((e) => setMessage(e.message));
              }}
            >
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} — {roleLabel(i.role)}
                </option>
              ))}
            </select>
          </label>
          {role.startsWith('aot_') && (
            <label>
              Operational access reason
              <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
            </label>
          )}
        </>
      )}
      {message && <p role="status">{message}</p>}
    </div>
  );
}
